import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { RootState } from "../../../redux/store";
import { ApiError } from "./types/common-types";
import {
  DGCoinCategoryState,
  DGCoinCategoryResponse,
  DGCoinCategory,
} from "./types/dgcoin-types";
import { PaginationReturn } from "./types/types";

const initialState: DGCoinCategoryState = {
  dgcoinCategories: [],
  dgcoinCategory: null,
  selectedDGCoinCategory: null,
  loading: true,
  error: null,
  success: false,
  total: 0,
  perPage: 15,
  currentPage: 1,
  lastPage: 1,
  pagination: {
    nextPageUrl: null,
    prevPageUrl: null,
  },
};

const API_URL = process.env.REACT_APP_API_URL;

export const createDGCoinCategory = createAsyncThunk<
  DGCoinCategoryResponse,
  FormData | Omit<DGCoinCategory, "id" | "created_at" | "updated_at">,
  { rejectValue: ApiError; state: RootState }
>("dgcoinCategories/create", async (dgcoinCategoryData, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const isFormData = dgcoinCategoryData instanceof FormData;
    const res = await axios.post(
      `${API_URL}/dg-coins-categories`,
      dgcoinCategoryData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(isFormData ? { "Content-Type": "multipart/form-data" } : {}),
        },
      }
    );
    return {
      message: res.data.message,
      dgcoinCategory: res.data.data,
    };
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to create DG Coin category" }
    );
  }
});

export const fetchDGCoinCategories = createAsyncThunk<
  DGCoinCategoryResponse & PaginationReturn,
  any | { url: string },
  { rejectValue: ApiError; state: RootState }
>("dgcoinCategories/fetchAll", async (urlFilters, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const url: string | undefined = urlFilters?.url;
    const apiUrl = url || `${API_URL}/dg-coins-categories`;
    const res = await axios.get(apiUrl, {
      params: urlFilters,
      headers: { Authorization: `Bearer ${token}` },
    });
    return {
      message: res.data.message,
      dgcoinCategories: res.data.data.data,
      total: res.data.data.total,
      per_page: res.data.data.per_page,
      current_page: res.data.data.current_page,
      last_page: res.data.data.last_page,
      prev_page_url: res.data.data.prev_page_url,
      next_page_url: res.data.data.next_page_url,
    };
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch DG Coin categories" }
    );
  }
});

export const fetchDGCoinCategoryById = createAsyncThunk<
  DGCoinCategory,
  number,
  { rejectValue: ApiError; state: RootState }
>("dgcoinCategories/fetchById", async (id, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.get(`${API_URL}/dg-coins-categories/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch DG Coin category" }
    );
  }
});

export const updateDGCoinCategory = createAsyncThunk<
  DGCoinCategory,
  { id: number; data: FormData | Partial<DGCoinCategory> },
  { rejectValue: ApiError; state: RootState }
>("dgcoinCategories/update", async ({ id, data }, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const isFormData = data instanceof FormData;
    const res = await axios.post(
      `${API_URL}/dg-coins-categories/${id}?_method=PUT`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(isFormData ? { "Content-Type": "multipart/form-data" } : {}),
        },
      }
    );
    return res.data.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to update DG Coin category" }
    );
  }
});

export const deleteDGCoinCategory = createAsyncThunk<
  number,
  number,
  { rejectValue: ApiError; state: RootState }
>("dgcoinCategories/delete", async (id, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    await axios.delete(`${API_URL}/dg-coins-categories/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return id;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to delete DG Coin category" }
    );
  }
});

const dgcoinCategorySlice = createSlice({
  name: "dgcoinCategories",
  initialState,
  reducers: {
    resetDGCoinCategoryState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
      state.selectedDGCoinCategory = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createDGCoinCategory.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(createDGCoinCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        if (action.payload.dgcoinCategory) {
          state.dgcoinCategories.unshift(action.payload.dgcoinCategory);
        }
      })
      .addCase(createDGCoinCategory.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to create DG Coin category";
      })
      .addCase(fetchDGCoinCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDGCoinCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.dgcoinCategories = action.payload.dgcoinCategories || [];
        state.total = action.payload.total || 0;
        state.perPage = action.payload.per_page || 15;
        state.currentPage = action.payload.current_page || 1;
        state.lastPage = action.payload.last_page || 1;
        state.pagination.nextPageUrl = action.payload.next_page_url;
        state.pagination.prevPageUrl = action.payload.prev_page_url;
      })
      .addCase(fetchDGCoinCategories.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to fetch DG Coin categories";
      })
      .addCase(fetchDGCoinCategoryById.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.selectedDGCoinCategory = null;
      })
      .addCase(fetchDGCoinCategoryById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedDGCoinCategory = action.payload;
      })
      .addCase(fetchDGCoinCategoryById.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to fetch DG Coin category";
      })
      .addCase(updateDGCoinCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateDGCoinCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.dgcoinCategories = state.dgcoinCategories.map((category) =>
          category.id === action.payload.id ? action.payload : category
        );
        if (state.selectedDGCoinCategory?.id === action.payload.id) {
          state.selectedDGCoinCategory = action.payload;
        }
      })
      .addCase(updateDGCoinCategory.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to update DG Coin category";
      })
      .addCase(deleteDGCoinCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteDGCoinCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.dgcoinCategories = state.dgcoinCategories.filter(
          (category) => category.id !== action.payload
        );
      })
      .addCase(deleteDGCoinCategory.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to delete DG Coin category";
      });
  },
});

export const { resetDGCoinCategoryState } = dgcoinCategorySlice.actions;
export default dgcoinCategorySlice.reducer;
